import { entrarConSesionCompartida, borrarSesion } from '../ui/sesion.js';
import Entrando from '../ui/Entrando.jsx';
import { useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback, useMemo } from 'react'
// Sistema de diseño de la suite: los tokens de color y las piezas comunes. Esta
// pantalla usaba estilos propios y se veía distinta al resto del CRM.
import { C } from '../ui/theme.js'
import { Pill, EmptyState, Th, Td, TableWrap } from '../ui/ui.jsx'
// La tarjeta de cifra premium se importa con alias porque este fichero ya tiene
// un `StatCard` propio (el que usan las cuatro cifras de arriba); ese envoltorio
// se mantiene con sus mismas props y por dentro pinta la del sistema.
import { StatCard as StatCardSistema, Ic } from '../ui/premium.jsx'

const SUPABASE_URL = 'https://zkznupgyaorojxpydlly.supabase.co'
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inprem51cGd5YW9yb2p4cHlkbGx5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjYwMjA5NTMsImV4cCI6MjA4MTU5Njk1M30.gKCpGxBeOCaaCRm10qQ_YpxXwTZQGbtTREsWrUG2RFY'

const supabase = {
  from: (table) => ({
    select: async (columns = '*', options = {}) => {
      let url = `${SUPABASE_URL}/rest/v1/${table}?select=${columns}`
      if (options.order) url += `&order=${options.order}`
      if (options.limit) url += `&limit=${options.limit}`
      try {
        const response = await fetch(url, {
          headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' }
        })
        if (!response.ok) throw new Error(`Error ${response.status}`)
        return { data: await response.json(), error: null }
      } catch (error) { return { data: null, error } }
    },
    insert: async (records) => {
      try {
        const response = await fetch(`${SUPABASE_URL}/rest/v1/${table}`, {
          method: 'POST',
          headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json', 'Prefer': 'return=representation' },
          body: JSON.stringify(Array.isArray(records) ? records : [records])
        })
        if (!response.ok) throw new Error(`Error ${response.status}`)
        return { data: await response.json(), error: null }
      } catch (error) { return { data: null, error } }
    }
  })
}

const supabaseAuth = {
  signIn: async (email, password) => {
    try {
      const response = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
        method: 'POST',
        headers: { 'apikey': SUPABASE_ANON_KEY, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      })
      if (!response.ok) {
        const error = await response.json()
        return { data: null, error: error.error_description || 'Credenciales incorrectas' }
      }
      const data = await response.json()
      localStorage.setItem('sb-access-token', data.access_token)
      localStorage.setItem('sb-refresh-token', data.refresh_token)
      return { data, error: null }
    } catch (error) { return { data: null, error: error.message } }
  },
  signOut: async () => {
    const token = localStorage.getItem('sb-access-token')
    if (token) { try { await fetch(`${SUPABASE_URL}/auth/v1/logout`, { method: 'POST', headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${token}` } }) } catch (e) {} }
    localStorage.removeItem('sb-access-token')
    localStorage.removeItem('sb-refresh-token')
  },
  getCurrentUser: async () => {
    const token = localStorage.getItem('sb-access-token')
    if (!token) return null
    try {
      const response = await fetch(`${SUPABASE_URL}/auth/v1/user`, { headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${token}` } })
      if (!response.ok) return null
      return await response.json()
    } catch { return null }
  }
}

const findAdminByAuthId = async (authUserId) => {
  try {
    const url = `${SUPABASE_URL}/rest/v1/administradores?auth_user_id=eq.${authUserId}&activo=eq.true&select=*`
    const response = await fetch(url, { headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` } })
    const data = await response.json()
    return data && data.length > 0 ? data[0] : null
  } catch { return null }
}

const PROVEEDORES_CONFIG = [
  { id: 'wallapop', nombre: 'Wallapop', email: 'news@news.wallapop.com', color: '#13C1AC', descripcion: 'Leads de vehículos publicados en Wallapop' },
  { id: 'web_dimoauto', nombre: 'Web Dimoauto', email: 'no-reply@stockspark.io', color: '#6B0000', descripcion: 'Formularios de contacto de la web corporativa' },
  { id: 'carnovo', nombre: 'Carnovo', email: 'concesionarios@carnovo.com', color: '#1E3A5F', descripcion: 'Portal de venta de vehículos nuevos' },
  { id: 'coches_net_vn', nombre: 'Coches.net VN', email: 'alvaro@dimoauto.com', color: '#FF6B35', descripcion: 'Coches.net - Vehículos Nuevos (VN). Recepción en alvaro@dimoauto.com' },
  { id: 'coches_net_vo', nombre: 'Coches.net VO', email: 'leads@coches.net', color: '#FF6B35', descripcion: 'Coches.net - Vehículos de Ocasión (VO)' }
]

function formatTimeAgo(dateString) {
  if (!dateString) return '-'
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)
  if (diffMins < 1) return 'Ahora'
  if (diffMins < 60) return `${diffMins} min`
  if (diffHours < 24) return `${diffHours}h`
  return `${diffDays}d`
}

function formatDateTime(dateString) {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

const AnimatedLoginBackground = forwardRef(({ cardRect }, ref) => {
  const canvasRef = useRef(null)
  const animationRef = useRef(null)
  const stateRef = useRef({ nodes: [], clickBoost: 0 })
  
  useImperativeHandle(ref, () => ({ triggerPulse: () => { stateRef.current.clickBoost = 1 } }))
  
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    let width, height
    const numNodes = 80, connectionDistance = 180, nodeSpeed = 0.3
    
    const createNodes = () => {
      const nodes = []
      for (let i = 0; i < numNodes; i++) {
        nodes.push({ x: Math.random() * width, y: Math.random() * height, vx: (Math.random() - 0.5) * nodeSpeed, vy: (Math.random() - 0.5) * nodeSpeed, radius: 2 + Math.random() * 2, baseRadius: 2 + Math.random() * 2 })
      }
      return nodes
    }
    
    const setup = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      width = window.innerWidth
      height = window.innerHeight
      canvas.width = width * dpr
      canvas.height = height * dpr
      canvas.style.width = width + 'px'
      canvas.style.height = height + 'px'
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      if (stateRef.current.nodes.length === 0) stateRef.current.nodes = createNodes()
    }
    
    setup()
    window.addEventListener('resize', setup)
    
    const animate = () => {
      const state = stateRef.current
      const nodes = state.nodes
      if (state.clickBoost > 0) state.clickBoost = Math.max(0, state.clickBoost - 0.015)
      
      const bgGradient = ctx.createLinearGradient(0, 0, width, height)
      bgGradient.addColorStop(0, '#0a1628')
      bgGradient.addColorStop(0.5, '#0d1f3c')
      bgGradient.addColorStop(1, '#081430')
      ctx.fillStyle = bgGradient
      ctx.fillRect(0, 0, width, height)
      
      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i]
        node.x += node.vx
        node.y += node.vy
        if (node.x < 0) { node.x = 0; node.vx *= -1 }
        if (node.x > width) { node.x = width; node.vx *= -1 }
        if (node.y < 0) { node.y = 0; node.vy *= -1 }
        if (node.y > height) { node.y = height; node.vy *= -1 }
        node.radius = node.baseRadius * (1 + state.clickBoost * 0.5)
      }
      
      ctx.lineWidth = 1
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x, dy = nodes[i].y - nodes[j].y
          const distance = Math.sqrt(dx * dx + dy * dy)
          if (distance < connectionDistance) {
            const opacity = (1 - distance / connectionDistance) * 0.6
            const boostedOpacity = Math.min(1, opacity + state.clickBoost * 0.3)
            const gradient = ctx.createLinearGradient(nodes[i].x, nodes[i].y, nodes[j].x, nodes[j].y)
            gradient.addColorStop(0, 'rgba(77, 130, 188, ' + boostedOpacity + ')')
            gradient.addColorStop(0.5, 'rgba(132, 182, 244, ' + (boostedOpacity * 0.8) + ')')
            gradient.addColorStop(1, 'rgba(77, 130, 188, ' + boostedOpacity + ')')
            ctx.strokeStyle = gradient
            ctx.beginPath()
            ctx.moveTo(nodes[i].x, nodes[i].y)
            ctx.lineTo(nodes[j].x, nodes[j].y)
            ctx.stroke()
          }
        }
      }
      
      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i]
        const glowRadius = node.radius * 4
        const glowGradient = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, glowRadius)
        const glowIntensity = 0.15 + state.clickBoost * 0.2
        glowGradient.addColorStop(0, 'rgba(132, 182, 244, ' + glowIntensity + ')')
        glowGradient.addColorStop(0.5, 'rgba(77, 130, 188, ' + (glowIntensity * 0.3) + ')')
        glowGradient.addColorStop(1, 'rgba(77, 130, 188, 0)')
        ctx.fillStyle = glowGradient
        ctx.beginPath()
        ctx.arc(node.x, node.y, glowRadius, 0, Math.PI * 2)
        ctx.fill()
        
        const nodeGradient = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, node.radius)
        nodeGradient.addColorStop(0, 'rgba(255, 255, 255, ' + (0.9 + state.clickBoost * 0.1) + ')')
        nodeGradient.addColorStop(0.3, 'rgba(196, 218, 250, ' + (0.8 + state.clickBoost * 0.1) + ')')
        nodeGradient.addColorStop(1, 'rgba(77, 130, 188, 0.6)')
        ctx.fillStyle = nodeGradient
        ctx.beginPath()
        ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2)
        ctx.fill()
      }
      
      if (cardRect) {
        const cardCenterX = cardRect.x + cardRect.width / 2
        const cardCenterY = cardRect.y + cardRect.height / 2
        const glowRadius = Math.max(cardRect.width, cardRect.height) * 0.8
        const cardGlow = ctx.createRadialGradient(cardCenterX, cardCenterY, 0, cardCenterX, cardCenterY, glowRadius)
        const intensity = 0.1 + state.clickBoost * 0.2
        cardGlow.addColorStop(0, 'rgba(0, 81, 135, ' + intensity + ')')
        cardGlow.addColorStop(0.5, 'rgba(77, 130, 188, ' + (intensity * 0.3) + ')')
        cardGlow.addColorStop(1, 'rgba(0, 81, 135, 0)')
        ctx.fillStyle = cardGlow
        ctx.fillRect(0, 0, width, height)
      }
      animationRef.current = requestAnimationFrame(animate)
    }
    
    stateRef.current.nodes = createNodes()
    animationRef.current = requestAnimationFrame(animate)
    
    return () => {
      window.removeEventListener('resize', setup)
      if (animationRef.current) cancelAnimationFrame(animationRef.current)
    }
  }, [cardRect])
  
  return <canvas ref={canvasRef} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }} />
})

function LoginScreen({ onLogin, isLoading, error }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [loginError, setLoginError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [forgotEmail, setForgotEmail] = useState('')
  const [forgotSent, setForgotSent] = useState(false)
  const [forgotLoading, setForgotLoading] = useState(false)
  const [cardRect, setCardRect] = useState(null)
  const cardRef = useRef(null)
  const canvasRef = useRef(null)
  
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail')
    const savedRemember = localStorage.getItem('rememberMe') === 'true'
    if (savedEmail && savedRemember) { setEmail(savedEmail); setRememberMe(true) }
  }, [])
  
  useEffect(() => {
    const updateCardRect = () => {
      if (cardRef.current) {
        const rect = cardRef.current.getBoundingClientRect()
        setCardRect({ x: rect.left, y: rect.top, width: rect.width, height: rect.height })
      }
    }
    updateCardRect()
    window.addEventListener('resize', updateCardRect)
    const timer = setTimeout(updateCardRect, 100)
    return () => { window.removeEventListener('resize', updateCardRect); clearTimeout(timer) }
  }, [])
  
  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoginError('')
    setIsSubmitting(true)
    if (canvasRef.current?.triggerPulse) canvasRef.current.triggerPulse()
    if (rememberMe) { localStorage.setItem('rememberedEmail', email); localStorage.setItem('rememberMe', 'true') } 
    else { localStorage.removeItem('rememberedEmail'); localStorage.removeItem('rememberMe') }
    try {
      const { data: authData, error: authError } = await supabaseAuth.signIn(email, password)
      if (authData && !authError) {
        const authUser = await supabaseAuth.getCurrentUser()
        if (authUser) { const admin = await findAdminByAuthId(authUser.id); if (admin) { onLogin(admin); return } }
      }
      setLoginError(authError || 'Email o contraseña incorrectos')
    } catch (err) { setLoginError('Error de conexión. Intenta de nuevo.') }
    setIsSubmitting(false)
  }
  
  const handleForgotPassword = async (e) => {
    e.preventDefault()
    setForgotLoading(true)
    try { await fetch(SUPABASE_URL + '/auth/v1/recover', { method: 'POST', headers: { 'apikey': SUPABASE_ANON_KEY, 'Content-Type': 'application/json' }, body: JSON.stringify({ email: forgotEmail }) }); setForgotSent(true) } 
    catch (err) { setForgotSent(true) }
    setForgotLoading(false)
  }
  
  // Campos y botones con las medidas del sistema: radio 10 en campos y botones,
  // primario en azul de marca con su sombra, secundario blanco con borde.
  const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 10, border: `1px solid ${C.line}`, fontSize: '13px', background: C.bg, color: C.ink, outline: 'none', boxSizing: 'border-box' }
  const labelStyle = { display: 'block', fontSize: '12px', fontWeight: 700, color: C.muted, marginBottom: '5px' }
  const btnPrimario = { border: 'none', borderRadius: 10, background: C.brand, color: '#fff', fontSize: '13px', fontWeight: 800, boxShadow: '0 3px 10px rgba(0,81,135,.28)' }
  const btnSecundario = { background: '#fff', border: `1px solid ${C.line}`, borderRadius: 10, color: C.page, fontSize: '13px', fontWeight: 800 }
  
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1f2a37', padding: '16px', position: 'relative', overflow: 'hidden' }}>
      <AnimatedLoginBackground ref={canvasRef} cardRect={cardRect} />
      <div ref={cardRef} style={{ width: '100%', maxWidth: '360px', background: 'rgba(255, 255, 255, 0.98)', borderRadius: '12px', padding: '28px', boxShadow: '0 25px 80px rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.1)', position: 'relative', zIndex: 1, backdropFilter: 'blur(10px)' }}>
        {/* Sin logotipo: el menú lateral de la suite ya pinta la marca al lado y
            verla dos veces era el defecto más visible del conjunto. */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <h1 style={{ color: C.ink, fontSize: '19px', fontWeight: 800, margin: '0 0 4px 0', letterSpacing: -0.2 }}>Email Leads</h1>
          <p style={{ color: C.muted, fontSize: '12.5px', margin: 0 }}>Grupo Dimoauto - Gestión de Proveedores</p>
        </div>
        
        {showForgotPassword ? (
          <div>
            <h2 style={{ fontSize: '15px', fontWeight: 800, color: C.ink, marginBottom: '6px' }}>Recuperar contraseña</h2>
            <p style={{ fontSize: '12.5px', color: C.muted, marginBottom: '14px' }}>Te enviaremos un enlace para restablecer tu contraseña.</p>
            {forgotSent ? (
              <div>
                <div style={{ background: `${C.verde}18`, border: `1px solid ${C.verde}44`, borderRadius: 10, padding: '12px', marginBottom: '14px', textAlign: 'center' }}>
                  <span style={{ color: '#1f7a44', fontSize: '12px' }}>Si el email existe, recibirás un enlace de recuperación.</span>
                </div>
                <button onClick={() => { setShowForgotPassword(false); setForgotSent(false); setForgotEmail('') }} style={{ ...btnSecundario, width: '100%', padding: '10px', cursor: 'pointer' }}>Volver al login</button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword}>
                <div style={{ marginBottom: '14px' }}><label style={labelStyle}>Email</label><input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="usuario@dimoauto.com" required style={inputStyle} /></div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button type="button" onClick={() => setShowForgotPassword(false)} style={{ ...btnSecundario, flex: 1, padding: '10px', cursor: 'pointer' }}>Cancelar</button>
                  <button type="submit" disabled={forgotLoading} style={{ ...btnPrimario, flex: 1, padding: '10px', cursor: forgotLoading ? 'wait' : 'pointer' }}>{forgotLoading ? 'Enviando...' : 'Enviar enlace'}</button>
                </div>
              </form>
            )}
          </div>
        ) : (
          <>
            {error && <div style={{ background: `${C.rojo}18`, border: `1px solid ${C.rojo}44`, borderRadius: 10, padding: '10px 12px', marginBottom: '14px' }}><span style={{ color: C.rojo, fontSize: '12px' }}>{error}</span></div>}
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '14px' }}><label style={labelStyle}>Email</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="usuario@dimoauto.com" required style={inputStyle} /></div>
              <div style={{ marginBottom: '12px' }}><label style={labelStyle}>Contraseña</label><input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required style={inputStyle} /></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '11px', color: C.muted }}>
                  <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} style={{ width: '14px', height: '14px', accentColor: C.brand, cursor: 'pointer' }} />Recordar credenciales
                </label>
                <button type="button" onClick={() => setShowForgotPassword(true)} style={{ background: 'none', border: 'none', color: C.brand, fontSize: '11px', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>¿Olvidaste tu contraseña?</button>
              </div>
              {loginError && <div style={{ background: `${C.rojo}18`, border: `1px solid ${C.rojo}44`, borderRadius: 10, padding: '10px 12px', marginBottom: '14px', textAlign: 'center' }}><span style={{ color: C.rojo, fontSize: '12px' }}>{loginError}</span></div>}
              <button type="submit" disabled={isSubmitting || isLoading} style={{ ...btnPrimario, width: '100%', padding: '11px', background: isSubmitting ? C.faint : C.brand, boxShadow: isSubmitting ? 'none' : btnPrimario.boxShadow, cursor: isSubmitting ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                {isSubmitting ? (<><span style={{ width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></span>Verificando...</>) : (<>🔒 Iniciar Sesión</>)}
              </button>
            </form>
          </>
        )}
        <div style={{ marginTop: '16px', padding: '8px', background: `${C.verde}14`, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: '10px', color: '#1f7a44' }}>🔒 Conexión segura con autenticación cifrada</span>
        </div>
      </div>
      <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
    </div>
  )
}

function Header({ admin, onLogout, onOpenChangePassword, onOpenConfig, isConnected }) {
  const [showUserMenu, setShowUserMenu] = useState(false)
  return (
    // Cabecera con la forma de la suite: sin logotipo (de la marca se encarga el
    // menú lateral y verla dos veces era el defecto más visible), título de 19 px
    // y una línea que explica qué se está mirando.
    <header style={{ background: '#fff', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14, flexWrap: 'wrap', borderBottom: `1px solid ${C.line}`, position: 'sticky', top: 0, zIndex: 100 }}>
      <div>
        <h1 style={{ fontSize: 19, fontWeight: 800, color: C.ink, margin: 0, letterSpacing: -0.2 }}>Email Leads</h1>
        <p style={{ fontSize: 12.5, color: C.muted, margin: '3px 0 0' }}>Leads que llegan por correo de cada portal: proveedores dados de alta, eventos del flujo en directo y últimas entradas.</p>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 10.5, fontWeight: 800, letterSpacing: 0.4, padding: '3px 10px', borderRadius: 999, color: isConnected ? C.verde : C.rojo, background: isConnected ? `${C.verde}18` : `${C.rojo}18`, border: `1px solid ${isConnected ? C.verde : C.rojo}44` }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: isConnected ? C.verde : C.rojo, animation: isConnected ? 'pulse 2s infinite' : 'none' }}></span>
          {isConnected ? 'LIVE' : 'Off'}
        </span>
        <button onClick={onOpenConfig} className="fg-nav" style={{ padding: '6px 12px', background: '#fff', border: `1px solid ${C.line}`, borderRadius: 10, fontSize: 13, cursor: 'pointer', color: C.page, fontWeight: 800 }}>Config</button>
        <div style={{ position: 'relative' }}>
          <button onClick={() => setShowUserMenu(!showUserMenu)} className="fg-nav" style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '4px 10px 4px 5px', background: '#fff', border: `1px solid ${C.line}`, borderRadius: 10, cursor: 'pointer' }}>
            <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'linear-gradient(135deg, #005187, #1a9cd4)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 800 }}>{admin?.nombre?.charAt(0) || 'A'}</div>
            <span style={{ fontSize: 12.5, color: C.page, fontWeight: 700 }}>{admin?.nombre || 'Admin'}</span>
          </button>
          {showUserMenu && (
            <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 6, background: '#fff', border: `1px solid ${C.line}`, borderRadius: 12, boxShadow: '0 14px 30px rgba(15,42,70,.12)', minWidth: 170, overflow: 'hidden', zIndex: 1000 }}>
              <button onClick={() => { onOpenChangePassword(); setShowUserMenu(false) }} className="fg-nav" style={{ width: '100%', padding: '9px 12px', background: 'none', border: 'none', textAlign: 'left', fontSize: 12.5, fontWeight: 700, color: C.page, cursor: 'pointer' }}>Cambiar contraseña</button>
              <hr style={{ margin: 0, border: 'none', borderTop: `1px solid ${C.line}` }} />
              <button onClick={onLogout} className="fg-nav" style={{ width: '100%', padding: '9px 12px', background: 'none', border: 'none', textAlign: 'left', fontSize: 12.5, fontWeight: 700, color: C.rojo, cursor: 'pointer' }}>Cerrar sesión</button>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}

function ChangePasswordModal({ onClose, admin }) {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  
  const handleChangePassword = async (e) => {
    e.preventDefault()
    setError('')
    if (newPassword.length < 6) { setError('La nueva contraseña debe tener al menos 6 caracteres'); return }
    if (newPassword !== confirmPassword) { setError('Las contraseñas no coinciden'); return }
    setIsLoading(true)
    try {
      const { error: verifyError } = await supabaseAuth.signIn(admin.email, currentPassword)
      if (verifyError) { setError('La contraseña actual es incorrecta'); setIsLoading(false); return }
      const token = localStorage.getItem('sb-access-token')
      const response = await fetch(SUPABASE_URL + '/auth/v1/user', { method: 'PUT', headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' }, body: JSON.stringify({ password: newPassword }) })
      if (!response.ok) { const errorData = await response.json(); throw new Error(errorData.message || 'Error al cambiar la contraseña') }
      setSuccess(true)
      setTimeout(() => onClose(), 2000)
    } catch (err) { setError(err.message || 'Error al cambiar la contraseña') }
    finally { setIsLoading(false) }
  }
  
  const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 10, border: `1px solid ${C.line}`, fontSize: '13px', color: C.ink, outline: 'none', boxSizing: 'border-box' }

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: '400px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', border: `1px solid ${C.line}`, overflow: 'hidden' }}>
        <div style={{ background: C.brand, padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, color: 'white', fontSize: '15px', fontWeight: 800 }}>Cambiar Contraseña</h3>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', width: '32px', height: '32px', borderRadius: '50%', cursor: 'pointer', fontSize: '16px' }}>✕</button>
        </div>
        <form onSubmit={handleChangePassword} style={{ padding: '24px' }}>
          {success ? (<div style={{ textAlign: 'center', padding: '20px' }}><p style={{ color: C.verde, fontWeight: 800 }}>Contraseña actualizada correctamente</p></div>) : (
            <>
              <div style={{ marginBottom: '14px' }}><label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: C.muted, marginBottom: '5px' }}>Contraseña actual</label><input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required style={inputStyle} /></div>
              <div style={{ marginBottom: '14px' }}><label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: C.muted, marginBottom: '5px' }}>Nueva contraseña</label><input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required style={inputStyle} /></div>
              <div style={{ marginBottom: '18px' }}><label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: C.muted, marginBottom: '5px' }}>Confirmar nueva contraseña</label><input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required style={inputStyle} /></div>
              {error && <div style={{ background: `${C.rojo}18`, border: `1px solid ${C.rojo}44`, padding: '10px 12px', borderRadius: 10, marginBottom: '16px', color: C.rojo, fontSize: '12.5px' }}>{error}</div>}
              <button type="submit" disabled={isLoading} style={{ width: '100%', padding: '11px', background: C.brand, border: 'none', borderRadius: 10, color: 'white', fontSize: '13px', fontWeight: 800, boxShadow: '0 3px 10px rgba(0,81,135,.28)', cursor: isLoading ? 'wait' : 'pointer' }}>{isLoading ? 'Guardando...' : 'Cambiar contraseña'}</button>
            </>
          )}
        </form>
      </div>
    </div>
  )
}

// Icono de cada tarjeta de cifra. Se resuelve aquí dentro, a partir del título,
// para no tener que tocar las llamadas que ya existen abajo.
const ICONO_STAT = {
  'Leads Hoy': <><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 6L2 7" /></>,
  'Leads Semana': <><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M8 2v4M16 2v4M3 10h18" /></>,
  'Proveedores': <><path d="M3 21h18" /><path d="M5 21V8l7-4 7 4v13" /></>,
  'Pendientes': <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
}

// La tarjeta de cifra de siempre, con las mismas props, pintada ya con la pieza
// del sistema (etiqueta en versalitas, cifra de 20 px que sube contando al entrar).
// El color que antes teñía el número ahora tiñe el icono: el número va en tinta,
// como en el resto de la suite, y el color sigue avisando de lo mismo.
function StatCard({ title, value, subtitle, color }) {
  // Si algún día el valor deja de ser un número (un "6/8", por ejemplo), se
  // pinta por `displayValue` en vez de quedarse en un guion.
  const numerico = value != null && value !== '' && !isNaN(value)
  return <StatCardSistema label={title} value={numerico ? value : null} displayValue={numerico ? undefined : value} sub={subtitle} color={color} icon={<Ic d={ICONO_STAT[title] || <circle cx="12" cy="12" r="8" />} />} />
}

function ProveedorCard({ proveedor, stats }) {
  const leadsHoy = stats?.leads_hoy || 0
  const leadsSemana = stats?.leads_semana || 0
  const ultimoLead = stats?.ultimo_lead
  return (
    // Tarjeta del sistema: borde fino, radio 12 y la elevación al pasar el ratón
    // la pone `fg-card` (antes iba escrita a mano con onMouseEnter/Leave).
    <div className="fg-card" style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', border: `1px solid ${C.line}` }}>
      <div style={{ background: proveedor.color, padding: '7px 8px', textAlign: 'center' }}>
        <h3 style={{ margin: 0, color: '#fff', fontSize: 12.5, fontWeight: 800 }}>{proveedor.nombre}</h3>
        <p style={{ margin: 0, color: 'rgba(255,255,255,0.85)', fontSize: 9.5 }}>{proveedor.email}</p>
      </div>
      <div style={{ padding: 8 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          <div style={{ textAlign: 'center', padding: '5px 3px', background: C.bg, borderRadius: 10 }}><p style={{ fontSize: 20, fontWeight: 800, color: C.ink, margin: 0, lineHeight: 1.05 }}>{leadsHoy}</p><p style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: C.muted, margin: 0 }}>Hoy</p></div>
          <div style={{ textAlign: 'center', padding: '5px 3px', background: C.bg, borderRadius: 10 }}><p style={{ fontSize: 20, fontWeight: 800, color: C.ink, margin: 0, lineHeight: 1.05 }}>{leadsSemana}</p><p style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: C.muted, margin: 0 }}>Semana</p></div>
        </div>
        <p style={{ fontSize: 11, color: C.muted, margin: '6px 0 0 0', textAlign: 'center' }}>Último: {ultimoLead ? formatTimeAgo(ultimoLead) : 'Sin leads'}</p>
      </div>
    </div>
  )
}

function LiveLogsPanel({ logs, isConnected, proveedores = [] }) {
  const [isExpanded, setIsExpanded] = useState(true)
  const logsContainerRef = useRef(null)

  // Solo mostramos logs de remitentes verificados (los que tienen tarjeta).
  // Excluimos DESCONOCIDO y SISTEMA (origen_codigo "desconocido" / "sistema"
  // o cualquier código que no esté en email_leads_origenes activos).
  const codigosValidos = useMemo(() => {
    const set = new Set()
    proveedores.forEach(p => {
      if (p?.id) set.add(String(p.id).toLowerCase())
      if (p?.nombre) set.add(String(p.nombre).toLowerCase())
    })
    return set
  }, [proveedores])

  const matchesProveedor = useCallback((log) => {
    if (!log) return false
    const candidatos = [log.origen_codigo, log.proveedor]
      .filter(Boolean)
      .map(v => String(v).toLowerCase())
    if (candidatos.length === 0) return false
    return candidatos.some(c => codigosValidos.has(c))
  }, [codigosValidos])

  const logsFiltrados = useMemo(
    () => (logs || []).filter(matchesProveedor),
    [logs, matchesProveedor]
  )

  useEffect(() => {
    // Scroll solo dentro del contenedor de logs, no de la página
    if (isExpanded && logsContainerRef.current) {
      logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight
    }
  }, [logsFiltrados, isExpanded])

  const [logsState, setLogsState] = useState(logsFiltrados)

  useEffect(() => {
    setLogsState(logsFiltrados)
  }, [logsFiltrados])
  
  const handleDownloadLogs = (e) => {
    e.stopPropagation()
    // Crear el contenido del archivo
    const logContent = logsState.map(log => 
      `[${log.timestamp}] ${log.proveedor || log.origen_codigo || 'SISTEMA'}: ${log.mensaje}`
    ).join('\n')
    
    // Crear blob y descargar
    const blob = new Blob([logContent], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `logs_email_leads_${new Date().toISOString().split('T')[0]}.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }
  
  // Colores y estilos por tipo
  const getLogStyle = (tipo) => {
    switch(tipo) {
      case 'success': return { color: '#2e9e5b', bg: 'rgba(34, 197, 94, 0.15)' }
      case 'error': return { color: '#d23b34', bg: 'rgba(239, 68, 68, 0.15)' }
      case 'warning': return { color: '#e08c1e', bg: 'rgba(245, 158, 11, 0.15)' }
      case 'info': return { color: '#1a9cd4', bg: 'rgba(59, 130, 246, 0.15)' }
      default: return { color: '#8a929c', bg: 'rgba(107, 114, 128, 0.15)' }
    }
  }
  
  return (
    // Sigue siendo una consola (fondo oscuro a propósito: son eventos en crudo),
    // pero con los radios del sistema: 12 en la tarjeta, 999 en las píldoras.
    <div style={{
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
      borderRadius: 12,
      overflow: 'hidden',
      marginBottom: 8,
      boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
      maxWidth: '48%',
      margin: '0 auto 8px'
    }}>
      {/* Header */}
      <div 
        onClick={() => setIsExpanded(!isExpanded)} 
        style={{ 
          padding: '5px 10px', 
          borderBottom: isExpanded ? '1px solid rgba(255,255,255,0.1)' : 'none', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          cursor: 'pointer', 
          userSelect: 'none',
          background: 'rgba(0,0,0,0.2)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <h3 style={{ margin: 0, color: '#fff', fontSize: 12.5, fontWeight: 800 }}>
            Monitor de Flujos
          </h3>
          <span style={{
            fontSize: 10.5,
            fontWeight: 800,
            letterSpacing: 0.4,
            color: C.muted,
            background: 'rgba(255,255,255,0.1)',
            padding: '2px 9px',
            borderRadius: 999
          }}>
            {logsState.length} eventos
          </span>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Botón Descargar logs */}
          {isExpanded && (
            <button 
              onClick={handleDownloadLogs}
              style={{
                padding: '4px 10px',
                borderRadius: 10,
                background: 'rgba(34, 197, 94, 0.2)',
                color: C.verde,
                fontSize: 11,
                cursor: 'pointer',
                fontWeight: 800,
                border: '1px solid rgba(34, 197, 94, 0.3)'
              }}
            >
              💾 Logs
            </button>
          )}
          
          {/* Indicador de conexión */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 6,
            padding: '3px 10px',
            background: isConnected ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
            borderRadius: 999,
            border: `1px solid ${isConnected ? 'rgba(34, 197, 94, 0.4)' : 'rgba(239, 68, 68, 0.4)'}`
          }}>
            <span style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: isConnected ? C.verde : C.rojo,
              animation: isConnected ? 'pulse 2s infinite' : 'none',
              boxShadow: isConnected ? `0 0 6px ${C.verde}` : 'none'
            }}></span>
            <span style={{
              fontSize: 10.5,
              color: isConnected ? C.verde : C.rojo,
              fontWeight: 800,
              letterSpacing: '0.4px'
            }}>
              {isConnected ? 'CONECTADO' : 'DESCONECTADO'}
            </span>
          </div>
          
          <span style={{ 
            color: '#8a929c', 
            fontSize: 14, 
            transition: 'transform 0.3s', 
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' 
          }}>▼</span>
        </div>
      </div>
      
      {/* Contenido */}
      {isExpanded && (
        <div 
          ref={logsContainerRef}
          style={{ 
            height: 170,
            overflowY: 'auto', 
            padding: 6,
            background: 'rgba(0,0,0,0.2)'
          }}>
          {logsState.length === 0 ? (
            // Mismo estado vacío del sistema (icono, título y pista), reescrito
            // aquí porque EmptyState va en tinta oscura y el panel es negro.
            <div style={{
              color: C.muted,
              textAlign: 'center',
              padding: '34px 24px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 6
            }}>
              <div style={{ fontSize: 30, opacity: 0.6 }}>📡</div>
              <div style={{ fontSize: 13.5, fontWeight: 800, color: '#e7e9ec' }}>Esperando eventos del flujo N8N...</div>
              <div style={{ fontSize: 12, opacity: 0.85 }}>Los logs aparecerán aquí en tiempo real</div>
            </div>
          ) : (
            // Vista simplificada - logs en una sola línea
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {logsState.map((log, index) => {
                const style = getLogStyle(log.tipo)
                // Reemplazar emoji de flujo completado y agregar "en Autolead"
                let mensaje = log.mensaje
                if (mensaje && mensaje.includes('Flujo completado')) {
                  mensaje = mensaje.replace('🎉', '✅')
                  if (mensaje.includes('registrado desde') && !mensaje.includes('en Autolead')) {
                    mensaje = mensaje + ' en Autolead'
                  }
                }
                return (
                  <div 
                    key={index} 
                    style={{ 
                      display: 'flex', 
                      gap: 8,
                      padding: '4px 10px',
                      background: style.bg,
                      borderRadius: 10,
                      borderLeft: `2px solid ${style.color}`,
                      animation: 'fadeIn 0.3s ease-out',
                      alignItems: 'center',
                      fontSize: 10
                    }}
                  >
                    <span style={{
                      color: style.color,
                      fontWeight: 800,
                      fontSize: 10,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      minWidth: 80
                    }}>
                      {log.proveedor || log.origen_codigo || 'SISTEMA'}
                    </span>
                    <span style={{ 
                      color: '#ffffff', 
                      fontSize: 11,
                      minWidth: 60
                    }}>
                      {log.timestamp}
                    </span>
                    <span style={{ 
                      color: '#e7e9ec', 
                      fontSize: 13,
                      flex: 1
                    }}>
                      {mensaje}
                    </span>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
      
      <style>{`
        @keyframes pulse { 
          0%, 100% { opacity: 1; } 
          50% { opacity: 0.5; } 
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  )
}

function LeadsTable({ leads, proveedores }) {
  const getProveedorInfo = (origen) => proveedores.find(p => p.nombre.toLowerCase() === origen?.toLowerCase() || p.id === origen?.toLowerCase()) || { nombre: origen, color: '#8a929c' }
  // Filtrado: la app Email Leads solo procesa los proveedores activos en su sección de tarjetas
  // (Wallapop, Web Dimoauto, Carnovo, Coches.net VN, Coches.net VO). Cualquier lead con otro
  // origen (CUSTOMER PEUGEOT, CUSTOMER CITROEN, CUSTOMER OPEL, Facebook, Instagram, etc.)
  // se muestra en sus apps específicas (Customer360, Meta Ads, etc.) y NUNCA aquí.
  const leadsFiltrados = leads.filter(l => {
    if (!l.origen) return false
    const origenLower = String(l.origen).toLowerCase().trim()
    // Exclusión explícita de orígenes que pertenecen a otras apps
    if (origenLower.startsWith('customer ')) return false
    if (['facebook', 'instagram', 'meta', 'google ads', 'web leapmotor'].includes(origenLower)) return false
    // Solo dejamos pasar leads cuyo origen coincide con un proveedor registrado en
    // email_leads_origenes (los de las tarjetas).
    return proveedores.some(p => {
      const nombre = p.nombre?.toLowerCase()
      const id = p.id?.toLowerCase()
      if (!nombre && !id) return false
      if (nombre === origenLower || id === origenLower) return true
      // Match laxo "coches.net*" -> Coches.net VN/VO (legacy sin sufijo)
      if (origenLower.startsWith('coches.net') && (id?.startsWith('coches_net') || nombre?.startsWith('coches.net'))) return true
      return false
    })
  })
  return (
    <div className="fg-card" style={{ background: '#fff', borderRadius: 12, border: `1px solid ${C.line}`, padding: 12 }}>
      <h2 style={{ fontSize: 14, fontWeight: 800, color: C.ink, margin: '2px 0 10px', letterSpacing: 0.2 }}>
        Leads Recientes <span style={{ color: C.muted, fontWeight: 600, fontSize: 11.5 }}>({leadsFiltrados.length} de {leads.length})</span>
      </h2>
      {leadsFiltrados.length === 0 ? (
        // Antes se veía una fila vacía dentro de la tabla; ahora, el estado
        // vacío del sistema, que explica además cuándo se llenará.
        <EmptyState icon="📭" title="No hay leads de los proveedores configurados"
          hint="En cuanto entre un correo de Wallapop, la web, Carnovo o Coches.net, el lead aparecerá aquí." />
      ) : (
        <TableWrap minWidth={880}>
          <thead><tr>{['Origen', 'Cliente', 'Contacto', 'Vehículo', 'Tipo', 'Asignación', 'Fecha'].map(h => (<Th key={h}>{h}</Th>))}</tr></thead>
          <tbody>
            {leadsFiltrados.map(lead => {
              const prov = getProveedorInfo(lead.origen)
              const colorEstado = lead.estado === 'pendiente' ? C.naranja : lead.estado === 'asignado' ? C.verde : C.rojo
              return (
                <tr key={lead.id}>
                  <Td><Pill color={prov.color} soft>{lead.origen || '-'}</Pill></Td>
                  <Td strong color={C.ink}>{lead.nombre} {lead.apellidos}</Td>
                  <Td>
                    <div>{lead.telefono || lead.movil || '-'}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{lead.email || '-'}</div>
                  </Td>
                  <Td>
                    <div style={{ fontWeight: 700, color: C.ink }}>{lead.marca || '-'}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{lead.modelo || '-'}</div>
                  </Td>
                  <Td><Pill soft>{lead.tipo_venta || '-'}</Pill></Td>
                  <Td><Pill color={colorEstado} soft>{lead.estado || 'pendiente'}</Pill></Td>
                  <Td color={C.muted}>{formatDateTime(lead.fecha_recepcion || lead.created_at)}</Td>
                </tr>
              )
            })}
          </tbody>
        </TableWrap>
      )}
    </div>
  )
}

function ConfiguracionModal({ proveedores, onSave, onClose }) {
  const [proveedoresLocal, setProveedoresLocal] = useState(proveedores)
  const [editingId, setEditingId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  
  const handleEmailChange = (id, newEmail) => setProveedoresLocal(prev => prev.map(p => p.id === id ? { ...p, email: newEmail } : p))
  
  const handleSave = async () => { 
    setSaving(true)
    setError(null)
    try {
      // Guardar cada proveedor en la BD
      for (const prov of proveedoresLocal) {
        if (prov.db_id) {
          await fetch(`${SUPABASE_URL}/rest/v1/email_leads_origenes?id=eq.${prov.db_id}`, {
            method: 'PATCH',
            headers: {
              'apikey': SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
              'Content-Type': 'application/json',
              'Prefer': 'return=minimal'
            },
            body: JSON.stringify({
              email_remitente: prov.email,
              updated_at: new Date().toISOString()
            })
          })
        }
      }
      onSave(proveedoresLocal)
      onClose()
    } catch (err) {
      setError('Error al guardar los cambios: ' + err.message)
    } finally {
      setSaving(false)
    }
  }
  
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: '700px', maxHeight: '80vh', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', border: `1px solid ${C.line}`, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ background: C.brand, padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, color: 'white', fontSize: '15px', fontWeight: 800 }}>Configuración de Proveedores</h3>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', width: '32px', height: '32px', borderRadius: '50%', cursor: 'pointer', fontSize: '16px' }}>✕</button>
        </div>
        <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
          <p style={{ fontSize: 12.5, color: C.muted, marginBottom: 16 }}>Configura los emails de los proveedores. Deben coincidir exactamente con los remitentes de los emails de leads.</p>
          {error && <div style={{ background: `${C.rojo}18`, border: `1px solid ${C.rojo}44`, padding: '10px 12px', borderRadius: 10, marginBottom: '16px', color: C.rojo, fontSize: '12.5px' }}>{error}</div>}
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><Th>Proveedor</Th><Th>Email Remitente</Th><Th align="center" w={100}>Acción</Th></tr></thead>
            <tbody>
              {proveedoresLocal.map(prov => (
                <tr key={prov.id}>
                  <Td><span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ width: 10, height: 10, borderRadius: 999, background: prov.color }}></span><span style={{ fontWeight: 700, color: C.ink }}>{prov.nombre}</span></span></Td>
                  <Td>{editingId === prov.id ? (<input type="email" value={prov.email} onChange={(e) => handleEmailChange(prov.id, e.target.value)} autoFocus style={{ width: '100%', padding: '8px 10px', borderRadius: 10, border: `1px solid ${C.brand}`, fontSize: 12.5, outline: 'none' }} />) : (<code style={{ background: C.bg, padding: '4px 9px', borderRadius: 999, fontSize: 11.5, color: C.page }}>{prov.email}</code>)}</Td>
                  <Td align="center">{editingId === prov.id ? (<button onClick={() => setEditingId(null)} style={{ padding: '6px 14px', background: C.verde, color: '#fff', border: 'none', borderRadius: 10, fontSize: 12.5, fontWeight: 800, cursor: 'pointer' }}>OK</button>) : (<button onClick={() => setEditingId(prov.id)} className="fg-nav" style={{ padding: '6px 14px', background: '#fff', color: C.page, border: `1px solid ${C.line}`, borderRadius: 10, fontSize: 12.5, fontWeight: 800, cursor: 'pointer' }}>Editar</button>)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ padding: '14px 20px', borderTop: `1px solid ${C.line}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} disabled={saving} className="fg-nav" style={{ padding: '10px 18px', background: '#fff', color: C.page, border: `1px solid ${C.line}`, borderRadius: 10, fontSize: 13, fontWeight: 800, cursor: saving ? 'wait' : 'pointer' }}>Cancelar</button>
          <button onClick={handleSave} disabled={saving} style={{ padding: '10px 18px', background: C.brand, color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 800, boxShadow: '0 3px 10px rgba(0,81,135,.28)', cursor: saving ? 'wait' : 'pointer' }}>{saving ? 'Guardando...' : 'Guardar cambios'}</button>
        </div>
      </div>
    </div>
  )
}

export default function EmailLeadsApp() {
  const [admin, setAdmin] = useState(null)

  // ── Sesión compartida de la suite (cookie de *.dimoauto.com) ──────────────
  // Si ya se entró en el Centro de Aplicaciones, aquí no se vuelve a pedir la
  // contraseña. Si no hay sesión compartida, o el correo no es administrador
  // activo, se enseña el login de siempre: esto solo añade un atajo.
  const [sesionProbada, setSesionProbada] = useState(false);
  useEffect(() => {
    let vivo = true;
    entrarConSesionCompartida(SUPABASE_URL, SUPABASE_ANON_KEY)
      .then((a) => { if (vivo && a) setAdmin(a); })
      .finally(() => { if (vivo) setSesionProbada(true); });
    return () => { vivo = false; };
  }, []);
  const [authLoading, setAuthLoading] = useState(true)
  const [loading, setLoading] = useState(true)
  const [showChangePassword, setShowChangePassword] = useState(false)
  const [showConfig, setShowConfig] = useState(false)
  const [leads, setLeads] = useState([])
  const [proveedores, setProveedores] = useState([])
  const [logs, setLogs] = useState([])
  const [isConnected, setIsConnected] = useState(false)
  
  useEffect(() => {
    const checkAuth = async () => {
      const authUser = await supabaseAuth.getCurrentUser()
      if (authUser) { const adminData = await findAdminByAuthId(authUser.id); if (adminData) setAdmin(adminData) }
      setAuthLoading(false)
    }
    checkAuth()
  }, [])
  
  // Scroll al top al cargar la app
  useEffect(() => {
    if (!authLoading && admin && !loading) {
      window.scrollTo(0, 0)
    }
  }, [authLoading, admin, loading])
  
  const loadData = useCallback(async () => {
    if (!admin) return
    setLoading(true)
    try {
      // Cargar proveedores desde email_leads_origenes
      const { data: proveedoresData, error: provError } = await supabase.from('email_leads_origenes').select('*', { order: 'id.asc' })
      if (provError) {
        console.error('Error cargando proveedores:', provError)
        addLog('error', 'SISTEMA', 'Error cargando proveedores: ' + provError.message)
      }
      if (proveedoresData) {
        console.log('Proveedores cargados:', proveedoresData)
        const proveedoresFormateados = proveedoresData.filter(p => p.activo).map(p => ({
          id: p.codigo,
          nombre: p.nombre,
          email: p.email_remitente,
          color: p.color,
          descripcion: p.descripcion,
          db_id: p.id
        }))
        console.log('Proveedores formateados:', proveedoresFormateados)
        setProveedores(proveedoresFormateados)
      }
      
      // Cargar leads
      const { data: inboxData, error: leadsError } = await supabase.from('AutoLead_inbox').select('*', { order: 'fecha_recepcion.desc,created_at.desc', limit: 200 })
      if (leadsError) {
        console.error('Error cargando leads:', leadsError)
        addLog('error', 'SISTEMA', 'Error cargando leads: ' + leadsError.message)
      }
      if (inboxData) {
        console.log('Leads cargados:', inboxData.length, 'registros')
        console.log('Primeros 3 leads:', inboxData.slice(0, 3))
        setLeads(inboxData)
      }
      
      // Cargar logs
      try {
        const { data: logsData } = await supabase.from('email_leads_logs').select('*', { order: 'created_at.desc', limit: 50 })
        if (logsData && logsData.length > 0) {
          const formattedLogs = logsData.map(log => ({ tipo: log.tipo, proveedor: log.origen_codigo?.toUpperCase() || 'SISTEMA', mensaje: log.mensaje, timestamp: new Date(log.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) })).reverse()
          setLogs(formattedLogs)
        }
      } catch (e) {
        console.error('Error cargando logs:', e)
      }
    } catch (error) { 
      console.error('Error loading data:', error)
      addLog('error', 'SISTEMA', 'Error cargando datos: ' + error.message) 
    }
    setLoading(false)
  }, [admin])
  
  const addLog = (tipo, proveedor, mensaje) => {
    const timestamp = new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    setLogs(prev => [...prev.slice(-49), { tipo, proveedor, mensaje, timestamp }])
  }
  
  useEffect(() => {
    if (!admin) return
    loadData()
    setIsConnected(true)
    addLog('success', 'SISTEMA', 'Conexión establecida - Realtime + Polling 60s')
    
    // Supabase Realtime para AutoLead_inbox
    let realtimeChannel = null
    const setupRealtime = () => {
      const wsUrl = SUPABASE_URL.replace('https://', 'wss://') + '/realtime/v1/websocket?apikey=' + SUPABASE_ANON_KEY + '&vsn=1.0.0'
      const ws = new WebSocket(wsUrl)
      
      ws.onopen = () => {
        console.log('Realtime conectado')
        // Suscribirse a cambios en AutoLead_inbox
        const joinMsg = JSON.stringify({
          topic: 'realtime:public:AutoLead_inbox',
          event: 'phx_join',
          payload: { config: { broadcast: { self: false }, presence: { key: '' }, postgres_changes: [{ event: '*', schema: 'public', table: 'AutoLead_inbox' }] } },
          ref: '1'
        })
        ws.send(joinMsg)
        
        // Heartbeat cada 30s
        const heartbeat = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ topic: 'phoenix', event: 'heartbeat', payload: {}, ref: Date.now().toString() }))
          }
        }, 30000)
        ws._heartbeat = heartbeat
      }
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          if (data.event === 'postgres_changes' && data.payload?.data) {
            const record = data.payload.data.record
            const eventType = data.payload.data.type
            if (eventType === 'INSERT') {
              addLog('success', record.origen?.toUpperCase() || 'NUEVO', 'Nuevo lead: ' + (record.nombre || 'Sin nombre') + ' - ' + (record.marca || '') + ' ' + (record.modelo || ''))
              loadData() // Recargar datos
            } else if (eventType === 'UPDATE') {
              addLog('info', 'SISTEMA', 'Lead actualizado: ' + (record.nombre || 'ID ' + record.id))
              loadData()
            }
          }
        } catch (e) {}
      }
      
      ws.onerror = () => { console.log('Error en Realtime, usando solo polling') }
      ws.onclose = () => { 
        if (ws._heartbeat) clearInterval(ws._heartbeat)
        // Reconectar después de 5s
        setTimeout(setupRealtime, 5000)
      }
      
      return ws
    }
    
    try { realtimeChannel = setupRealtime() } catch (e) { console.log('Realtime no disponible') }
    
    // Polling cada 60 segundos como respaldo
    const interval = setInterval(() => loadData(), 60000)
    
    return () => { 
      clearInterval(interval)
      setIsConnected(false)
      if (realtimeChannel && realtimeChannel.readyState === WebSocket.OPEN) {
        realtimeChannel.close()
      }
    }
  }, [admin, loadData])
  
  const handleLogin = (adminData) => { setAdmin(adminData); addLog('success', 'SISTEMA', 'Login exitoso: ' + adminData.nombre) }
  const handleLogout = async () => { borrarSesion(); await supabaseAuth.signOut(); setAdmin(null); setLeads([]); setLogs([]) }
  const handleSaveProveedores = (newProveedores) => { setProveedores(newProveedores); addLog('success', 'SISTEMA', 'Configuración de proveedores actualizada'); loadData() }
  
  // Calcular stats desde los leads actuales
  const proveedorStats = useMemo(() => {
    const now = new Date()
    const hoy = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const hace7dias = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
    const stats = {}
    
    console.log('Calculando stats con:', leads.length, 'leads y', proveedores.length, 'proveedores')
    
    proveedores.forEach(prov => {
      const provLeads = leads.filter(l => {
        const origenLower = l.origen?.toLowerCase()
        const nombreLower = prov.nombre?.toLowerCase()
        const idLower = prov.id?.toLowerCase()
        const match = origenLower === nombreLower || origenLower === idLower
        
        if (match) {
          console.log(`Lead match encontrado: origen="${l.origen}" con proveedor="${prov.nombre}" (id: ${prov.id})`)
        }
        
        return match
      })
      
      const leadsHoy = provLeads.filter(l => new Date(l.fecha_recepcion || l.created_at) >= hoy).length
      const leadsSemana = provLeads.filter(l => new Date(l.fecha_recepcion || l.created_at) >= hace7dias).length
      
      console.log(`Proveedor ${prov.nombre}: ${provLeads.length} leads totales, ${leadsHoy} hoy, ${leadsSemana} semana`)
      
      stats[prov.id] = {
        leads_hoy: leadsHoy,
        leads_semana: leadsSemana,
        ultimo_lead: provLeads[0]?.fecha_recepcion || provLeads[0]?.created_at || null
      }
    })
    
    console.log('Stats calculados:', stats)
    
    return stats
  }, [leads, proveedores])
  
  const totalLeadsHoy = Object.values(proveedorStats).reduce((sum, s) => sum + (s.leads_hoy || 0), 0)
  const totalLeadsSemana = Object.values(proveedorStats).reduce((sum, s) => sum + (s.leads_semana || 0), 0)
  const proveedoresActivos = proveedores.length
  const leadsPendientes = leads.filter(l => l.estado === 'pendiente').length
  
  // Espera de la comprobación de sesión: el punto que respira de la suite, en
  // vez del anillo giratorio que no se parecía a nada del sistema.
  if (authLoading) return (<div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, background: C.ink, color: '#c4dafa', fontSize: 13.5, fontWeight: 600 }}><span className="fg-breath" style={{ width: 9, height: 9, borderRadius: '50%', background: '#1a9cd4' }} />Verificando sesión...</div>)
  if (!admin && !sesionProbada) return <Entrando />;
  if (!admin) return <LoginScreen onLogin={handleLogin} isLoading={false} error={null} />
  // Primera carga: en vez de un spinner centrado, el esqueleto de la pantalla
  // que va a aparecer (cuatro cifras, el monitor y la tabla).
  if (loading && leads.length === 0) return (
    <div style={{ minHeight: '100vh', background: C.bg, padding: '14px 14px 20px', maxWidth: 1600, margin: '0 auto' }}>
      <div className="fg-shimmer" style={{ height: 46, borderRadius: 12, marginBottom: 12 }} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 10 }}>
        {[0, 1, 2, 3].map(i => <div key={i} className="fg-shimmer" style={{ height: 74, borderRadius: 12 }} />)}
      </div>
      <div className="fg-shimmer" style={{ height: 200, borderRadius: 12, marginBottom: 10, maxWidth: '48%', margin: '0 auto 10px' }} />
      <div style={{ display: 'flex', justifyContent: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        {[0, 1, 2, 3, 4].map(i => <div key={i} className="fg-shimmer" style={{ height: 96, borderRadius: 12, width: 'calc(20% - 6px)', minWidth: 140 }} />)}
      </div>
      <div className="fg-shimmer" style={{ height: 320, borderRadius: 12 }} />
    </div>
  )
  
  console.log('Renderizando dashboard con:', { 
    leads: leads.length, 
    proveedores: proveedores.length,
    totalLeadsHoy,
    totalLeadsSemana,
    leadsPendientes
  })
  
  return (
    <div className="fg-fade" style={{ minHeight: '100vh', background: C.bg }}>
      <style>{"@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } } @keyframes spin { to { transform: rotate(360deg); } } table { width: 100%; border-collapse: collapse; } tbody tr:hover { background: #f7f8fa !important; } select:focus, button:focus { outline: none; }"}</style>
      <Header admin={admin} onLogout={handleLogout} onOpenChangePassword={() => setShowChangePassword(true)} onOpenConfig={() => setShowConfig(true)} isConnected={isConnected} />
      <div style={{ padding: '12px 14px 18px', maxWidth: 1600, margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 10 }}>
          <StatCard title="Leads Hoy" value={totalLeadsHoy} subtitle="Recibidos hoy" color="#005187" />
          <StatCard title="Leads Semana" value={totalLeadsSemana} subtitle="Últimos 7 días" color="#005187" />
          <StatCard title="Proveedores" value={proveedoresActivos} subtitle="Configurados" color="#2e9e5b" />
          <StatCard title="Pendientes" value={leadsPendientes} subtitle="Por asignar" color={leadsPendientes > 10 ? '#e08c1e' : '#2e9e5b'} />
        </div>
        <LiveLogsPanel logs={logs} isConnected={isConnected} proveedores={proveedores} />
        <div style={{ marginBottom: 10 }}>
          <h2 style={{ fontSize: 14, fontWeight: 800, color: C.ink, margin: '2px 0 10px', letterSpacing: 0.2, textAlign: 'center' }}>Estado por Proveedor</h2>
          {proveedores.length === 0 ? (
            // Sin proveedores activos no se pintaba nada y la pantalla parecía rota.
            <EmptyState icon="🏷️" title="Ningún proveedor activo"
              hint="Da de alta el remitente de cada portal en «Config» para que sus leads se reconozcan al llegar." />
          ) : (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>{proveedores.map(prov => (<div key={prov.id} style={{ width: 'calc(20% - 8px)', minWidth: 140 }}><ProveedorCard proveedor={prov} stats={proveedorStats[prov.id]} /></div>))}</div>
          )}
        </div>
        <LeadsTable leads={leads} proveedores={proveedores} />
        <footer style={{ marginTop: 12, paddingTop: 8, textAlign: 'center', color: C.faint, fontSize: 11 }}><p style={{ margin: 0 }}>Email Leads | Grupo Dimoauto</p></footer>
      </div>
      {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} admin={admin} />}
      {showConfig && <ConfiguracionModal proveedores={proveedores} onSave={handleSaveProveedores} onClose={() => setShowConfig(false)} />}
    </div>
  )
}
